import React, { useState, useEffect, useContext, createContext, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { Session, User } from '@supabase/supabase-js';

// Definindo a interface para o perfil do usuário
interface UserProfile {
  id: string;
  subscription_status: 'free' | 'pro' | 'inactive' | 'basic'; // Adicionando 'basic'
  full_name: string | null;
  country: string | null;
  role?: 'user' | 'admin'; // Adicionando role para controle de acesso admin
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null; // Adicionando o perfil
  isLoading: boolean;
}

// Definindo o contexto com um valor inicial undefined
const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Função auxiliar para buscar o perfil
const fetchProfile = async (userId: string): Promise<UserProfile | null> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, subscription_status, full_name, country, role')
    .eq('id', userId)
    .single();

  if (error && error.code !== 'PGRST116') { // PGRST116 = No rows found
    console.error('Error fetching profile:', error);
    return null;
  }

  // Se o perfil não existir, retornamos um perfil padrão 'free' para evitar bloqueio total
  if (!data) {
    return {
      id: userId,
      subscription_status: 'free',
      full_name: null,
      country: null,
    };
  }

  return data as UserProfile;
};

// Status de assinatura válidos para acesso ao app
// const VALID_SUBSCRIPTION_STATUSES = ['pro', 'basic']; // Only 'basic' and 'pro' allowed

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const handleAuthChange = async (currentSession: Session | null) => {
      setSession(currentSession);
      const currentUser = currentSession?.user ?? null;
      setUser(currentUser);

      let userProfile: UserProfile | null = null;

      if (currentUser) {
        userProfile = await fetchProfile(currentUser.id);

        // --- Subscription Validation Note ---
        // We no longer automatically log out users with invalid subscriptions here.
        // Instead, the App.tsx shows a blocking modal that prevents access
        // while keeping the user logged in so they can see contact options.
      }

      setProfile(userProfile);
      setIsLoading(false);
    };

    // 1. Obter a sessão inicial
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleAuthChange(session);
    });

    // 2. Escutar mudanças de estado (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        // Se o evento for 'SIGNED_OUT', tratamos imediatamente
        if (_event === 'SIGNED_OUT') {
          setUser(null);
          setSession(null);
          setProfile(null);
          setIsLoading(false);
        } else {
          // Para outros eventos (SIGNED_IN, TOKEN_REFRESHED), usamos a lógica completa
          handleAuthChange(session);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, profile, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};